"use client";

import clsx from "clsx";
import React, { useEffect, useId, useState, forwardRef } from "react";
import countries from "world-countries";
import { CountryCode, getCountryCallingCode, isSupportedCountry } from "libphonenumber-js";
import { FieldLabel } from "~/components/Forms";
import { FaPhone } from "react-icons/fa";
import { getDefaultCountry, getFlagForCountry, supportedCountryCodes } from "@/utils/locales";
import { Locale } from "@/i18n-config";

export const PhoneField = forwardRef<HTMLInputElement, Omit<React.ComponentPropsWithoutRef<"input">, "id"> & {
    lang: Locale;
    label?: string;
    dictionary?: {
        formCountriesSuggested?: string;
        formCountriesOthers?: string;
    };
    defaultCountry?: string;
    defaultPhone?: string;
    onValueChangeAction?: (value: { phone: string; country: string }) => void;
    labelClassName?: string;
    inputClassName?: string;
    error?: any;
    note?: string;
    noteClassName?: string;
    light?: boolean;
}>(({
    lang,
    label,
    dictionary,
    defaultCountry,
    defaultPhone,
    onValueChangeAction,
    className,
    labelClassName,
    inputClassName,
    error,
    note,
    noteClassName,
    light,
    ...props
}, ref) => {
    const [phone, setPhone] = useState<string>(defaultPhone || "");
    const [country, setCountry] = useState<string>(defaultCountry || getDefaultCountry());

    const id = useId();

    useEffect(() => {
        if (onValueChangeAction) onValueChangeAction({ phone, country });
    }, [phone, country, onValueChangeAction]);

    const enrichedCountries = countries
    .filter((c) => isSupportedCountry(c.cca2 as CountryCode))
    .map((c) => ({
        ...c,
        localizedName: c.name.common,
        phoneCode: `+${getCountryCallingCode(c.cca2 as CountryCode)}`,
    }));

    const suggestedCountries = enrichedCountries
    .filter((c) => supportedCountryCodes.includes(c.cca2))
    .sort((a, b) => a.localizedName.localeCompare(b.localizedName));

    const otherCountries = enrichedCountries
    .filter((c) => !supportedCountryCodes.includes(c.cca2))
    .sort((a, b) => a.localizedName.localeCompare(b.localizedName));

    return (
        <div className={className}>
            {label && (
                <FieldLabel id={id} className={labelClassName} required={Boolean(props.required)}>
                    {label}
                </FieldLabel>
            )}
            <div className="relative">
        <span
            className={clsx(
                "absolute z-10 inset-y-0 left-0 w-12 flex items-center justify-center rounded-l-md focus:outline-none",
                error
                    ? "bg-rose-200 text-rose-400"
                    : (light ? "bg-zinc-200 text-zinc-500" : "bg-zinc-100 text-zinc-300")
            )}>
          <FaPhone size="22" />
        </span>
                <div className="relative flex flex-row gap-0">
                    <select
                        id={`country-${id}`}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        disabled={Boolean(props.disabled)}
                        className={clsx(
                            // absolutely positioned country picker inside the input
                            "absolute z-[11] left-12 w-[94px] appearance-none !bg-transparent py-2.5 px-3 border-0 text-lg ring-0 focus:outline-none focus:ring-0",
                            error
                                ? "text-rose-700 placeholder:text-rose-400"
                                : (light ? "text-zinc-900 placeholder:text-zinc-400" : "text-zinc-900 placeholder:text-zinc-400"),
                            "placeholder:text-zinc-400 disabled:select-none disabled:text-zinc-500 disabled:cursor-not-allowed"
                        )}
                        style={{ backgroundImage: "none" }}>
                        <optgroup label={dictionary?.formCountriesSuggested || "Countries"}>
                            {suggestedCountries.map((c) => (
                                <option key={`cs-${id}-${c.cca2}`} value={c.cca2}>
                                    {getFlagForCountry(c.cca2)} {c.phoneCode}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{c.localizedName}
                                </option>
                            ))}
                        </optgroup>
                        <optgroup label={dictionary?.formCountriesOthers || "Other countries"}>
                            {otherCountries.map((c) => (
                                <option key={`co-${id}-${c.cca2}`} value={c.cca2}>
                                    {getFlagForCountry(c.cca2)} {c.phoneCode}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{c.localizedName}
                                </option>
                            ))}
                        </optgroup>
                    </select>
                    <input
                        id={`phone-${id}`}
                        ref={ref}
                        type="tel"
                        aria-label="tel-national"
                        aria-invalid={!!error}
                        onChange={(e) => setPhone(e.target.value)}
                        className={clsx(
                            // centralized base + error styles
                            "field",
                            error && "field-error",
                            // space for icon + country select overlay
                            "pl-[140px] pr-3",
                            inputClassName
                        )}
                        {...props}
                    />
                </div>
            </div>
            {note && <div className={clsx("field-note", noteClassName)}>{note}</div>}
            {error && <div className="field-error-text">{error}</div>}
        </div>
    );
});
